import { Form, FormSubmission, FormAnalytics } from '@/components/form-builder/types'

const BASE = import.meta.env.VITE_API_URL ?? ''

const hdrs = (): HeadersInit => {
  const token    = localStorage.getItem('auth_token') ?? ''
  const storeUrl = localStorage.getItem('shop_domain') ?? ''
  return {
    'Content-Type':  'application/json',
    'Authorization': `Bearer ${token}`,
    'store-url':     storeUrl,
  }
}

async function apiFetch<T>(url: string, opts: RequestInit = {}): Promise<T> {
  const res  = await fetch(`${BASE}${url}`, { ...opts, headers: { ...hdrs(), ...(opts.headers ?? {}) } })
  const data = await res.json()
  if (!res.ok) throw new Error(data.message ?? `API error ${res.status}`)
  return data
}

// ── Forms ──────────────────────────────────────────────────────────────────────

export const fetchForms = () =>
  apiFetch<{ forms: Form[]; file_upload_locked?: boolean }>('/api/forms')

export const fetchForm = (id: number) =>
  apiFetch<{ form: Form }>(`/api/forms/${id}`).then(d => d.form)

export const saveForm = (form: Partial<Form>) =>
  apiFetch<{ form: Form }>(
    form.id ? `/api/forms/${form.id}` : '/api/forms',
    { method: form.id ? 'PUT' : 'POST', body: JSON.stringify(form) },
  ).then(d => d.form)

export const deleteForm = (id: number) =>
  apiFetch<void>(`/api/forms/${id}`, { method: 'DELETE' })

export const duplicateForm = (id: number) =>
  apiFetch<{ form: Form }>(`/api/forms/${id}/duplicate`, { method: 'POST' }).then(d => d.form)

// ── Submissions ────────────────────────────────────────────────────────────────

export const fetchSubmissions = (
  formId: number,
  params: { status?: string; search?: string; page?: number; per_page?: number } = {},
) => {
  const q = new URLSearchParams(Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)])
  )).toString()
  return apiFetch<{ submissions: FormSubmission[]; total: number; pages: number; unread: number; pending: number }>(
    `/api/forms/${formId}/submissions${q ? '?' + q : ''}`
  )
}

export const updateSubmissionStatus = (id: number, status: string) =>
  apiFetch<void>(`/api/forms/submissions/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) })

export const deleteSubmission = (id: number) =>
  apiFetch<void>(`/api/forms/submissions/${id}`, { method: 'DELETE' })

export const bulkDeleteSubmissions = (formId: number, ids: number[]) =>
  apiFetch<void>(`/api/forms/${formId}/submissions/bulk`, { method: 'DELETE', body: JSON.stringify({ ids }) })

export const anonymizeSubmission = (id: number) =>
  apiFetch<void>(`/api/forms/submissions/${id}/anonymize`, { method: 'POST' })

export const exportCsv = async (formId: number) => {
  const res = await fetch(`${BASE}/api/forms/${formId}/submissions/export`, { headers: hdrs() })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.message ?? `Export failed (${res.status})`)
  }
  const blob = await res.blob()
  const filename = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? `submissions-${formId}.csv`
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// ── Approval ───────────────────────────────────────────────────────────────────

export const approveSubmission = (id: number, note: string) =>
  apiFetch<{ shopify_sync: { success: boolean; message: string | null } | null }>(`/api/forms/submissions/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  })

export const rejectSubmission = (id: number, note: string) =>
  apiFetch<void>(`/api/forms/submissions/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  })

// ── Analytics ──────────────────────────────────────────────────────────────────

export const fetchAnalytics = (formId: number, days = 30) =>
  apiFetch<{ status: string } & FormAnalytics>(`/api/forms/${formId}/analytics?days=${days}`)

// ── Embed key ──────────────────────────────────────────────────────────────────

export const generateEmbedKey = () =>
  apiFetch<{ status: string; public_key: string; mobile_config: unknown; website_embed: string }>('/api/forms/generate-key', { method: 'POST' })
